import debounce from "lodash/debounce";
import { setupScrollSpy } from "./utils/setupScrollSpy";

// ─── Constants ────────────────────────────────────────────────────────────────

const CLASSES = {
  active: "is-active",
  open: "is-open",
  animationStart: "animation-start",
  showHamburger: "hamburger-on-desktop",
  landingNavType: "enh-nav__landing-navigation",
  activeOnly: "active",
};

const INIT_ATTR = "data-enh-nav-hover-initialized";

const SELECTORS = {
  header: ".enhanced-navigation",
  logo: ".comp-logo a",
  serviceNavigationContent: ".enh-nav__service-navigation-content",
  linksBar: ".enh-nav__links-bar",
  linksBarContent: ".enh-nav__links-bar-content",
  linkContainer: ".enh-nav__links-container",
  lvl1LinkWrapper: ".enh-nav__branch--level-1",
  lvl2LinkWrapper: ".enh-nav__branch--level-2",
  dropDown: ".enh-nav__drop-down",
  dropDownContent: ".enh-nav__drop-down-content",
  lvl3navWrapper: ".enh-nav__level-3-wrapper",
  focusDropdownButton: ".enh-nav__focus-dropdown-button",
  lvl2FocusDropdownButton:
    ".enh-nav__link-container--secondary .enh-nav__focus-dropdown-button",
  link: ".enh-nav__link",
  linkContent: ".enh-nav__link-text",
  logoWrapper: ".enh-nav__logo",
  quickLinksWrapper: ".enh-nav__quick-links",
};

// ─── Class ────────────────────────────────────────────────────────────────────

export class EnhancedHoverNavigation {
  private el: HTMLElement;
  private abortController: AbortController;

  // Module-level state in original — promoted to instance fields for proper encapsulation
  private isOpenActiveTabCalled = true;
  private isTabOpen = false;
  private isLandingNavType: boolean;

  // Cached elements
  private header: HTMLElement | null;
  private logo: HTMLAnchorElement | null;
  private linksBar: HTMLElement | null;
  private linksBarContent: HTMLElement | null;
  private linkContainer: HTMLElement | null;
  private mainLinkWrappers: HTMLElement[];
  private focusDropdownButtons: HTMLElement[];
  private lvl2FocusDropdownButtons: HTMLElement[];
  private lvl2LinkWrappers: HTMLElement[];
  private serviceNavContent: HTMLElement | null;
  private logoWrapper: HTMLElement | null;
  private quickLinksWrapper: HTMLElement | null;

  // Width measurements (set in setInitialWidthValues / refreshed in initialNavElementsWidth)
  private linksBarWidth = 0;
  private serviceNavigationContentWidth = 0;
  private logoWidth = 0;
  private quickLinksWidth = 0;

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();

    this.isLandingNavType =
      el.parentElement?.classList.contains(CLASSES.landingNavType) ?? false;

    // document-scoped — may live outside the component root
    this.header = document.querySelector<HTMLElement>(SELECTORS.header);
    this.logo = document.querySelector<HTMLAnchorElement>(SELECTORS.logo);


    this.linksBar = el.querySelector<HTMLElement>(SELECTORS.linksBar);
    this.linksBarContent = el.querySelector<HTMLElement>(SELECTORS.linksBarContent);
    this.linkContainer = el.querySelector<HTMLElement>(SELECTORS.linkContainer);
    this.mainLinkWrappers = Array.from(
      this.linksBar?.querySelectorAll<HTMLElement>(SELECTORS.lvl1LinkWrapper) ?? [],
    );
    this.focusDropdownButtons = Array.from(
      el.querySelectorAll<HTMLElement>(SELECTORS.focusDropdownButton),
    );
    this.lvl2FocusDropdownButtons = Array.from(
      el.querySelectorAll<HTMLElement>(SELECTORS.lvl2FocusDropdownButton),
    );
    this.lvl2LinkWrappers = Array.from(
      this.linksBar?.querySelectorAll<HTMLElement>(SELECTORS.lvl2LinkWrapper) ?? [],
    );
    this.serviceNavContent = el.querySelector<HTMLElement>(
      SELECTORS.serviceNavigationContent,
    );
    this.logoWrapper = el.querySelector<HTMLElement>(SELECTORS.logoWrapper);
    this.quickLinksWrapper = el.querySelector<HTMLElement>(SELECTORS.quickLinksWrapper);
  }

  init(): void {
    this.setInitialWidthValues();
    this.bindEvents();
    this.checkIfLinksBarIsFullyVisible();
  }

  destroy(): void {
    this.abortController.abort();
  }

  // ─── Event binding ──────────────────────────────────────────────────────────

  private bindEvents(): void {
    const { signal } = this.abortController;

    if (this.isLandingNavType) {
      this.initialNavElementsWidth();
      this.checkIfLinksBarIsFullyVisible();
      this.setupNavigationScrollSpy(signal);

      if (this.linksBarWidth === 0) {
        window.addEventListener(
          "resize",
          debounce(() => this.initialNavElementsWidth(), 200),
          { signal },
        );
      }
      window.addEventListener(
        "resize",
        debounce(() => this.checkIfLinksBarIsFullyVisible(), 200),
        { signal },
      );
    } else {
      this.switchToHamburger();
      // viewportChange (Terrific) → window resize
      window.addEventListener("resize", () => this.switchToHamburger(), { signal });
    }

    this.handleLinksBarHover(signal);
    this.handleLvl2linkHover(signal);
    this.bindCloseDropDowns(signal);
    this.bindEscapeKeyPress(signal);
    this.bindKeyboardToggleDropdown(signal);
  }

  // ─── Scroll spy (landing nav only) ───────────────────────────────────────────

  private setupNavigationScrollSpy(signal: AbortSignal): void {
    const links = Array.from(
      this.el.querySelectorAll<HTMLAnchorElement>(SELECTORS.link),
    );
    setupScrollSpy(links, signal);
  }

  // ─── Width measurements ───────────────────────────────────────────────────────

  private setInitialWidthValues(): void {
    this.linksBarWidth = this.linksBar?.offsetWidth ?? 0;
    this.serviceNavigationContentWidth = this.serviceNavContent?.offsetWidth ?? 0;
    this.logoWidth = this.logoWrapper?.offsetWidth ?? 0;
    this.quickLinksWidth = this.quickLinksWrapper?.offsetWidth ?? 0;
  }

  private initialNavElementsWidth(): void {
    const bodyWidth = document.documentElement.clientWidth;

    if (this.linksBarWidth <= 0 && bodyWidth > 1024) {
      this.serviceNavigationContentWidth =
        this.el.querySelector<HTMLElement>(SELECTORS.serviceNavigationContent)
          ?.offsetWidth ?? 0;
      this.logoWidth =
        this.el.querySelector<HTMLElement>(SELECTORS.logoWrapper)?.offsetWidth ?? 0;
      this.quickLinksWidth =
        this.el.querySelector<HTMLElement>(SELECTORS.quickLinksWrapper)?.offsetWidth ?? 0;

      this.header?.classList.remove(CLASSES.showHamburger);
      this.linksBarWidth = this.linksBar?.offsetWidth ?? 0;

      this.checkIfLinksBarIsFullyVisible();
    }
  }

  private checkIfLinksBarIsFullyVisible(): void {
    if (!this.isLandingNavType || this.linksBarWidth === 0) return;

    const availableWidth = Math.floor(
      this.serviceNavigationContentWidth - this.logoWidth - this.quickLinksWidth,
    );

    if (this.linksBarWidth > availableWidth) {
      this.header?.classList.add(CLASSES.showHamburger);
    }
  }

  private switchToHamburger(): void {
    const bodyWidth = document.documentElement.clientWidth;
    const headerHasShowHamburger =
      this.header?.classList.contains(CLASSES.showHamburger) ?? false;
    // 64 accounts for the space needed for the search element
    const dropDownNavContentWidth = 64 + (this.linkContainer?.offsetWidth ?? 0);
    const dropDownNavWidth = this.linksBarContent?.offsetWidth ?? 0;

    if (bodyWidth < 1226 || headerHasShowHamburger) return;

    if (dropDownNavContentWidth > dropDownNavWidth) {
      this.header?.classList.add(CLASSES.showHamburger);
    }
  }

  // ─── Body overlay ────────────────────────────────────────────────────────────

  private showBodyOverlay(): void {
    document.body.style.overflow = "hidden";
    this.linksBar?.classList.add(CLASSES.open, CLASSES.animationStart);
  }

  private hideBodyOverlay(): void {
    document.body.style.overflow = "initial";
    this.linksBar?.classList.remove(CLASSES.animationStart, CLASSES.open);
  }

  // ─── Links-bar hover ────────────────────────────────────────────────────────

  private handleLinksBarHover(signal: AbortSignal): void {
    this.mainLinkWrappers.forEach((wrapper) => {
      wrapper.addEventListener(
        "mouseenter",
        () => {
          if (!this.isTabOpen) {
            this.closeActiveTab();
            this.openActiveTab(wrapper);
          }
          this.isTabOpen = false;
        },
        { signal },
      );
    });
  }

  // ─── Level-2 link hover ──────────────────────────────────────────────────────

  private handleLvl2linkHover(signal: AbortSignal): void {
    this.lvl2LinkWrappers.forEach((wrapper) => {
      wrapper.addEventListener(
        "mouseenter",
        () => {
          this.lvl2LinkWrappers.forEach((link) => {
            link.classList.remove(CLASSES.active, CLASSES.animationStart);
          });
          this.showLevel3(wrapper);
        },
        { signal },
      );
    });
  }

  // ─── Close dropdowns on mouseleave ───────────────────────────────────────────

  private bindCloseDropDowns(signal: AbortSignal): void {
    this.linkContainer?.addEventListener(
      "mouseleave",
      () => {
        if (this.isOpenActiveTabCalled && !this.isTabOpen) {
          this.closeActiveTab();
          this.hideBodyOverlay();
          this.isOpenActiveTabCalled = false;
        }
        this.isTabOpen = false;
      },
      { signal },
    );
  }

  // ─── Open / close active tab ─────────────────────────────────────────────────

  private openActiveTab(lvl1wrapper: HTMLElement): void {
    const dropDown = lvl1wrapper.querySelector<HTMLElement>(SELECTORS.dropDown);

    if (dropDown) {
      this.showBodyOverlay();
      lvl1wrapper.classList.add(CLASSES.active, CLASSES.animationStart);
      this.isOpenActiveTabCalled = true;
    } else {
      this.hideBodyOverlay();
    }
  }

  private closeActiveTab(): void {
    const activeBranch = this.el.querySelector<HTMLElement>(
      `${SELECTORS.lvl1LinkWrapper}.${CLASSES.active}`,
    );
    if (!activeBranch) return;

    const dropDown = activeBranch.querySelector<HTMLElement>(SELECTORS.dropDown);
    activeBranch.classList.remove(CLASSES.animationStart, CLASSES.active);

    if (dropDown) {
      dropDown.classList.remove(CLASSES.animationStart);
      dropDown
        .querySelectorAll<HTMLElement>(SELECTORS.dropDownContent)
        .forEach((el) => {
          el.style.height = "initial";
        });
      dropDown
        .querySelectorAll<HTMLElement>(SELECTORS.lvl2LinkWrapper)
        .forEach((el) => el.classList.remove(CLASSES.active, CLASSES.animationStart));
    }
  }

  // ─── Level-3 reveal ──────────────────────────────────────────────────────────

  private showLevel3(level2wrapper: HTMLElement): void {
    const lvl3nav = level2wrapper.querySelector<HTMLElement>(SELECTORS.lvl3navWrapper);
    const dropDownContent =
      (level2wrapper.parentElement?.parentElement as HTMLElement | null) ?? null;

    if (dropDownContent) dropDownContent.style.height = "initial";
    level2wrapper.classList.add(CLASSES.active, CLASSES.animationStart);

    if (lvl3nav && dropDownContent) {
      const lvl3navHeight = lvl3nav.offsetHeight;
      const dropDownHeight = dropDownContent.offsetHeight;
      if (lvl3navHeight >= dropDownHeight) {
        dropDownContent.style.height = `${lvl3navHeight}px`;
      }
    }
  }

  // ─── Keyboard navigation ─────────────────────────────────────────────────────

  private bindKeyboardToggleDropdown(signal: AbortSignal): void {
    this.focusDropdownButtons.forEach((btn) => {
      btn.addEventListener(
        "keydown",
        (e: KeyboardEvent) => {
          if (e.code !== "Enter" && e.code !== "Space") return;

          const wrapper2 = btn.closest<HTMLElement>(SELECTORS.lvl2LinkWrapper);
          const wrapper1 = btn.closest<HTMLElement>(SELECTORS.lvl1LinkWrapper);

          if (wrapper2) {
            this.toggleLevel3(wrapper2);
          } else if (wrapper1) {
            this.toggleLevel2(wrapper1);
            this.isTabOpen = true;
          }
        },
        { signal },
      );
    });
  }

  private bindEscapeKeyPress(signal: AbortSignal): void {
    this.linksBar?.addEventListener(
      "keydown",
      (e: KeyboardEvent) => {
        if (e.code === "Escape") {
          this.closeActiveTab();
          this.hideBodyOverlay();
          this.logo?.focus();
        }
      },
      { signal },
    );
  }

  // ─── Level-2 / level-3 toggle (keyboard) ────────────────────────────────────

  private setLvl2FocusDropdownButtonsPosition(): void {
    this.lvl2FocusDropdownButtons.forEach((button) => {
      // siblings(selectors.link) equivalent — first sibling matching the selector
      const link = Array.from(button.parentElement?.children ?? []).find(
        (child) => child !== button && child.matches(SELECTORS.link),
      ) as HTMLElement | null;
      const linkContent = link?.querySelector<HTMLElement>(SELECTORS.linkContent);

      // getBoundingClientRect().width mirrors jQuery's .css("width") fallback behaviour:
      // getComputedStyle().width returns "auto" for inline/abs-positioned elements,
      // causing parseInt("auto") = NaN. getBoundingClientRect always returns a number.
      const contentWidth = Math.floor(linkContent?.getBoundingClientRect().width ?? 0);
      const buttonWidth = Math.floor(button.getBoundingClientRect().width);
      button.style.left = `${contentWidth + buttonWidth + 4}px`;
    });
  }

  private toggleLevel2(lvl1wrapper: HTMLElement): void {
    const isActive = lvl1wrapper.classList.contains(CLASSES.active);
    this.closeActiveTab();

    if (isActive) {
      this.hideBodyOverlay();
    } else {
      this.openActiveTab(lvl1wrapper);
      this.setLvl2FocusDropdownButtonsPosition();
    }
  }

  private toggleLevel3(level2wrapper: HTMLElement): void {
    const isActive = level2wrapper.classList.contains(CLASSES.active);
    const dropDownContent =
      (level2wrapper.parentElement?.parentElement as HTMLElement | null) ?? null;

    this.lvl2LinkWrappers.forEach((link) =>
      link.classList.remove(CLASSES.active, CLASSES.animationStart),
    );

    if (!isActive) {
      this.showLevel3(level2wrapper);
    } else if (dropDownContent) {
      dropDownContent.style.height = "initial";
    }
  }

  // ─── Static bootstrap ───────────────────────────────────────────────────────

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="enhanced-hover-navigation"]')
      .forEach((el) => {
        if (el.hasAttribute(INIT_ATTR)) return;
        el.setAttribute(INIT_ATTR, "true");
        new EnhancedHoverNavigation(el).init();
      });
  }
}
